// 261010 UI2 (L6) — ba lỗ sửa tay ở ngăn Sửa bước Video: tải clip lên · cắt đầu/đuôi clip đang chọn · video tham chiếu `@Video` (CHỈ engine 2.5, tối đa 1).
// Mỗi thao tác có xác nhận, không hiện giá. Luật kiểm ở workflowL6Utils (thuần, khớp BE). Gỡ dấu sửa tay diễn xuất nằm ở thẻ Diễn xuất (bước Đạo diễn).
import React, { useRef, useState } from 'react';
import { Alert, Button, Modal, Popconfirm, Space, message } from 'antd';
import { filmApi } from '../../services/api/film';
import type { FilmEvent } from '../../types/film';
import { useAliveRef } from '../../hooks/useAliveRef';
import ClipTrimModal, { type TrimTarget } from './ClipTrimModal';
import { clipFileCheck, manualEditErrorText, trimSummaryOf, videoRefGateOf, videoRefOf, videoRefTokenOf } from './workflowL6Utils';
import { needsConfirm } from './workflowConfirmPolicy';

interface Props { storyId: string; eventId: string; eventOrder?: number; ev: FilmEvent; onActionDone: () => void }

const WorkflowVideoL6Panel: React.FC<Props> = ({ storyId, eventId, eventOrder, ev, onActionDone }) => {
  const alive = useAliveRef();
  const clipInput = useRef<HTMLInputElement>(null);
  const refInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [trim, setTrim] = useState<TrimTarget | null>(null);
  const name = `Video ${eventOrder ?? ''}`.trim();
  const selected = (ev.clips ?? []).find((c) => c.id === ev.selectedClipId) ?? null;
  const ref = videoRefOf(ev);
  const gate = videoRefGateOf(ev);

  const pick = (onFile: (f: File) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    const chk = clipFileCheck(f);
    if (chk.ok === false) { setErr(chk.message); return; }
    setErr(null); onFile(f);
  };
  const run = async (key: string, what: string, fn: () => Promise<void>) => {
    setBusy(key); setErr(null);
    try { await fn(); onActionDone(); }
    catch (e) { if (alive.current) setErr(manualEditErrorText(e, what)); }
    finally { if (alive.current) setBusy(null); }
  };

  const doUploadClip = (f: File) => run('clip', 'Tải clip lên', async () => { const r = await filmApi.uploadEventClip(eventId, f); message.success(r?.selected ? 'Đã tải clip lên và chọn làm bản đang dùng.' : 'Đã tải clip lên — bấm "Chọn bản" nếu muốn dùng.'); });
  // U9: tải clip lên là thêm một bản mới (không phá huỷ, không qua máy tạo clip) ⇒ không hỏi.
  const uploadClip = (f: File) => (!needsConfirm('VIDEO', 'UPLOAD') ? doUploadClip(f) : Modal.confirm({
    title: `Tải clip lên cho ${name}?`, okText: 'Tải lên', cancelText: 'Huỷ',
    content: `File "${f.name}" sẽ thành một bản mới của video (mp4, 1–180 giây). Không qua máy tạo clip. Nếu video chưa có bản nào đang chọn, bản này được chọn luôn.`,
    onOk: () => run('clip', 'Tải clip lên', async () => { const r = await filmApi.uploadEventClip(eventId, f); message.success(r?.selected ? 'Đã tải clip lên và chọn làm bản đang dùng.' : 'Đã tải clip lên — bấm "Chọn bản" nếu muốn dùng.'); }),
  }));
  const uploadRef = (f: File) => Modal.confirm({
    title: `Gắn video tham chiếu cho ${name}?`, okText: 'Tải lên và gắn', cancelText: 'Huỷ',
    content: `File "${f.name}" (mp4, 2–30 giây) được tải lên truyện và gắn làm @Video của video này (chỉ Seedance 2.5). Clip đã tạo trước đó sẽ thành "cũ" vì dùng tham chiếu khác.`,
    onOk: () => run('ref', 'Gắn video tham chiếu', async () => {
      const up = await filmApi.uploadStoryRefVideo(storyId, f);
      await filmApi.setEventVideoRef(eventId, up.key);
      message.success('Đã gắn video tham chiếu — chèn chip «Video tham chiếu» ở bước Prompt để dùng.');
    }),
  });

  return (
    <section className="wf-l6" data-testid="wf-l6-panel" aria-label="Sửa tay clip và video tham chiếu">
      <div className="wf-l6-row">
        <b>Clip</b>
        <Space wrap size={8}>
          <input ref={clipInput} type="file" accept=".mp4,video/mp4" style={{ display: 'none' }} onChange={pick(uploadClip)} data-testid="wf-l6-clip-file" />
          <Button size="small" loading={busy === 'clip'} disabled={busy !== null} onClick={() => clipInput.current?.click()} data-testid="wf-l6-upload-clip">Tải clip lên</Button>
          <Button size="small" disabled={!selected || !(selected.durationS && selected.durationS > 0) || busy !== null}
            title={!selected ? 'Chưa chọn bản nào để cắt' : !(selected.durationS && selected.durationS > 0) ? 'Clip chưa có thời lượng nên chưa cắt được' : undefined}
            onClick={() => selected && setTrim({ clipId: selected.id, label: `${name} · bản đang chọn`, durationS: selected.durationS ?? null, cutInS: selected.cutInS ?? null, cutOutS: selected.cutOutS ?? null })} data-testid="wf-l6-trim">Cắt đầu / đuôi</Button>
          {selected ? <span className="muted" style={{ fontSize: 12 }} data-testid="wf-l6-trim-note">{trimSummaryOf(selected.durationS, selected.cutInS, selected.cutOutS)}</span> : null}
        </Space>
      </div>
      <div className="wf-l6-row">
        <b>@Video</b>
        <Space wrap size={8}>
          <input ref={refInput} type="file" accept=".mp4,video/mp4" style={{ display: 'none' }} onChange={pick(uploadRef)} data-testid="wf-l6-ref-file" />
          {ref ? (
            <>
              <video src={ref.url} controls preload="none" style={{ height: 64, borderRadius: 6, background: '#000' }} />
              <span className="mono" style={{ fontSize: 12 }}>{ref.durationS ? `${ref.durationS} s` : 'video'} · {videoRefTokenOf(ref).slice(0, 40)}…</span>
              <Popconfirm title={`Gỡ video tham chiếu của ${name}?`} description="Clip đã tạo sẽ thành cũ. Chip @Video trong prompt (nếu có) nên xoá." okText="Gỡ" cancelText="Huỷ"
                onConfirm={() => { void run('unref', 'Gỡ video tham chiếu', async () => { await filmApi.setEventVideoRef(eventId, null); message.success('Đã gỡ video tham chiếu.'); }); }}>
                <Button size="small" danger loading={busy === 'unref'} disabled={busy !== null} data-testid="wf-l6-unref">Gỡ</Button>
              </Popconfirm>
            </>
          ) : (
            <Button size="small" loading={busy === 'ref'} disabled={gate.ok === false || busy !== null} title={gate.ok === false ? gate.reason : undefined} onClick={() => refInput.current?.click()} data-testid="wf-l6-upload-ref">Tải video tham chiếu</Button>
          )}
        </Space>
      </div>
      {gate.ok === false && !ref ? <div className="muted" style={{ fontSize: 12 }} data-testid="wf-l6-ref-why">{gate.reason}</div> : null}
      {err ? <Alert type="error" showIcon style={{ marginTop: 8 }} message={err} closable onClose={() => setErr(null)} data-testid="wf-l6-error" /> : null}
      <ClipTrimModal target={trim} onClose={() => setTrim(null)} onSaved={onActionDone} />
    </section>
  );
};

export default WorkflowVideoL6Panel;
